import { Vue, Component, Watch } from "vue-property-decorator";
import { fmtMsg, MessageHelper, NotificationType } from "commonui";
import { REPLocale, CommonLocale } from "@/locales/localeid";
import { AccessLevel, LanguageSkill, MediaSlot, MethodologyType, MethodologyTypeEnum, ParentType } from "@/util/enum";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { REPModule } from "@/modules/rep";
import { Table, Icon, Modal, Select, Button, Form } from "ant-design-vue";
import Spinning from "@/components/spinning/spinning.vue";
import DurationSelectRange from "@/components/content-exposure/duration-select-range/duration-select-range.vue";
import ContentExposureActivity from "@/components/content-exposure/content-exposure-activity/content-exposure-activity.vue";
import ContentExposureObjective from "@/components/content-exposure/content-exposure-objective/content-exposure-objective.vue";
import ContentExposureRelatedItem from "@/components/content-exposure/content-exposure-relatedItem/content-exposure-relatedItem.vue";
import ContentExposureAction from "@/components/content-exposure/content-exposure-action/content-exposure-action.vue";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import SlotRadioGroup from "@/components/content-exposure/slot-radio-group/slot-radio-group.vue";
import { ILessonPlanVersionSimpleModel } from "@/models/lesson-plan";
import { ILessonPlanVersionService, LessonPlanVersionService } from "@/services/lesson-plan";
import { VersionGroupModule } from "@/modules/versiongroup";
import { ContentExposureModel } from "@/services/teaching-activity";
import { Methodology, MethodologyTypeItems } from "@/util/constants";
import ManageOrderDrawer from "@/components/content-exposure/manage-order-drawer/manage-order-drawer.vue";
import { cloneDeep } from "lodash";

const numberOfRepExposures = 1;
@Component({
	components: {
		Icon,
		Table,
		Spinning,
		DurationSelectRange,
		ContentExposureObjective,
		ContentExposureActivity,
		ContentExposureRelatedItem,
		ContentExposureAction,
		SlotRadioGroup,
		Select,
		Option: Select.Option,
		Modal,
		Button,
		Form,
		Item: Form.Item,
		ManageOrderDrawer
	}
})
export default class REPList extends Vue {
	public columns: any = [
		{
			title: fmtMsg(REPLocale.REPDuration),
			dataIndex: "duration",
			key: "duration",
			width: 178,
			scopedSlots: { customRender: "duration" }
		},
		{
			title: fmtMsg(REPLocale.REPSlot),
			dataIndex: "mediaSlots",
			key: "mediaSlots",
			scopedSlots: { customRender: "mediaSlots" }
		},
		{
			title: fmtMsg(REPLocale.REPRelatedItems),
			dataIndex: "relatedItems",
			key: "relatedItems",
			class: "tt-rep__related-items",
			scopedSlots: { customRender: "relatedItems" }
		},
		{
			title: fmtMsg(REPLocale.REPTeachingActivity),
			dataIndex: "teachingActivities",
			key: "teachingActivities",
			scopedSlots: { customRender: "teachingActivities" }
		},
		{
			title: fmtMsg(REPLocale.REPObjective),
			dataIndex: "objectives",
			key: "objectives",
			scopedSlots: { customRender: "objectives" }
		},
		{
			title: "",
			dataIndex: "action",
			key: "action",
			width: 75,
			align: "center",
			customCell: () => ({
				class: "tt-rep__body-col-action"
			}),
			scopedSlots: { customRender: "action" }
		}
	];

	public loading = false;
	public isREP = true;
	public recordInEditMode: string | null = null;
	public titleListModule = getModule(TitleListState);
	public currentMethodology = MethodologyTypeEnum.REP;
	public lessonPlanVersions: ILessonPlanVersionSimpleModel[] = [];
	public selectedLPV: ILessonPlanVersionSimpleModel = {
		id: "",
		name: "",
		description: "",
		methodology: -1,
		status: -1,
		versionGroupId: "",
		combinedNameMethodology: ""
	};
	lessonPlanVersionService: ILessonPlanVersionService = LessonPlanVersionService();
	public isDuplicateModalVisible = false;
	public repExposureDuplicateForm;
	public duplicateTargetLPVId: string = "";
	public targetDuplicateLPVs: ILessonPlanVersionSimpleModel[] = [];
	public duplicating: boolean = false;
	public exposureLoading: boolean = false;
	public commonLocale = CommonLocale;
	public manageOrderDrawerVisible: boolean = false;
	public currentSelectedExposureId: string | null = null;
	public filteredLPVs: ILessonPlanVersionSimpleModel[] = [];
	public selectedLPVId: string = "";

	created() {
		this.repExposureDuplicateForm = this.$form.createForm(this, { name: "repExposureDuplicateForm" });
	}

	async mounted() {
		const selectedVersionGroups = [REPModule.getVersionGroupId];
		const methodologyTypes = [MethodologyTypeEnum.REP];
		this.lessonPlanVersions = await this.lessonPlanVersionService.getLessonPlanVersionsByTypes(
			selectedVersionGroups,
			methodologyTypes
		);

		this.lessonPlanVersions.forEach((lpv: ILessonPlanVersionSimpleModel) => {
			lpv.combinedNameMethodology = `${lpv.name}(${this.methodology})`;
		});

		if (this.lessonPlanVersions.length) {
			this.selectedLPV = this.lessonPlanVersions[0];
			this.selectedLPVId = this.lessonPlanVersions[0].id;
			this.filteredLPVs = this.lessonPlanVersions;
		}

		if (this.lessonPlanVersions.length > 1) {
			this.targetDuplicateLPVs = this.lessonPlanVersions.filter((lpv) => lpv.id !== this.selectedLPV.id);
			this.duplicateTargetLPVId = this.targetDuplicateLPVs[0].id;
		}
	}

	@Watch("lessonPlanVersions")
	updateFilteredLPVs() {
		this.filteredLPVs = cloneDeep(this.lessonPlanVersions);
	}

	@Watch("selectedLPV", { immediate: true })
	async setTargetDuplicateLPVs() {
		if (this.lessonPlanVersions.length > 1 && this.selectedLPV.id !== "") {
			this.targetDuplicateLPVs = this.lessonPlanVersions.filter((lpv) => lpv.id !== this.selectedLPV.id);
			this.duplicateTargetLPVId = this.targetDuplicateLPVs[0].id;
		}

		if (this.selectedLPV.id !== "") {
			this.loadREPExposures();
		}
	}

	async loadREPExposures() {
		this.exposureLoading = true;
		await REPModule.fetchREPs(this.selectedLPV.id);
		this.exposureLoading = false;
	}

	get REPModule() {
		return REPModule;
	}

	get repLocale() {
		return REPLocale;
	}

	get contentId() {
		const { editeContentSelectedChapterId, editedContentId } = this.titleListModule;
		// If the component/Title is a Parent we need to return contentId (GL-10176)
		if (this.titleListModule.isParent) {
			return editedContentId;
		} else {
			return editeContentSelectedChapterId !== "none" ? editeContentSelectedChapterId : editedContentId;
		}
	}

	get contentTitle() {
		const {
			editedContentDetails: { title }
		} = this.titleListModule;
		return title;
	}

	get isEditing() {
		return this.repList.some((d) => d.isEdit);
	}

	get repList() {
		return REPModule.getREPs;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	async onAddREP() {
		this.loading = true;
		const lessonPlanVersionId = this.selectedLPV.id;
		const currentMediaSlot = await REPModule.getDefaultMediaSlot();

		const records = (await REPModule.addREP({
			lessonPlanVersionId,
			numberOfRepExposures,
			currentMediaSlot
		}).catch((_) => Promise.reject((this.loading = false)))) as any;
		this.loading = false;
		if (records.length > 0) {
			this.recordInEditMode = records[0].id;
			REPModule.editingExposure({ ...records[0], isEdit: true } as any);
			this.scrollBottom();
		}
	}

	onRadioMediaSlotChange(value) {
		REPModule.setMediaSlot(value);
	}

	onDeleteREP(record) {
		Modal.confirm({
			title: fmtMsg(REPLocale.REPWarningDelete),
			content: "",
			onOk: async () => {
				this.loading = true;
				await REPModule.deleteREP(record).catch((_) => (this.loading = false));
				this.loading = false;
			},
			onCancel: () => {}
		});
	}

	async onCopyREP(recordIds: any[], isBulk: boolean) {
		this.loading = true;
		let targetLessonPlanVersionId = this.duplicateTargetLPVId;
		const lessonPlanVersionId = this.selectedLPV.id;

		if (!isBulk) {
			targetLessonPlanVersionId = this.selectedLPV.id;
		}
		const result = await REPModule.copyREP({ recordIds, lessonPlanVersionId, targetLessonPlanVersionId }).catch(
			(_) => Promise.reject((this.loading = false))
		);
		this.loading = false;
		if (result.length > 0) {
			if (!isBulk) {
				this.recordInEditMode = result[0].id;
				REPModule.editingExposure({ ...result[0], isEdit: true } as any);
				this.scrollBottom();
			}
		}
	}

	async onEditREP(record, isEdit) {
		if (this.recordInEditMode && this.recordInEditMode !== record.id) {
			return;
		}
		if (isEdit) {
			this.recordInEditMode = record.id;
			record.isEdit = isEdit;
			REPModule.editingExposure(record);
		} else {
			this.recordInEditMode = null;
			this.loadREPExposures();
		}
	}

	exposureHasErrors() {
		return Object.keys(this.$refs).some((key) => (this.$refs[key] as any)?.showError);
	}

	async onSaveREP(record) {
		if (this.exposureHasErrors()) {
			return;
		}

		this.loading = true;
		await REPModule.updateExposure(record).catch((_) => (this.loading = false));
		this.recordInEditMode = null;
		this.loading = false;
	}

	scrollBottom() {
		this.$nextTick(() => {
			const dom = document.querySelector("main.vc-layout__content.ant-layout-content") as HTMLDivElement;
			if (dom) {
				dom.style.scrollBehavior = "smooth";
				dom.scrollTop = dom.scrollHeight;
				dom.style.scrollBehavior = "inherit";
			}
		});
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	handleLPVChange(lpvId: string) {
		this.selectedLPVId = lpvId;
		this.selectedLPV = this.findLessonPlanVersionById(lpvId);
	}

	findLessonPlanVersionById(lpvId: string): ILessonPlanVersionSimpleModel {
		return this.lessonPlanVersions.find((lpv) => lpv.id === lpvId) || ({} as ILessonPlanVersionSimpleModel);
	}

	handleLPVSearch(value: any) {
		this.selectedLPVId = "";
		this.filteredLPVs = this.lessonPlanVersions.filter((lpv: any) => {
			const combinedNameMethodology = lpv.combinedNameMethodology.toLowerCase();
			return combinedNameMethodology.includes(value.toLowerCase());
		});
	}

	get methodology(): string {
		return MethodologyTypeItems.get(MethodologyType.REP) || Methodology.REP;
	}

	onDuplicateREP() {
		this.isDuplicateModalVisible = true;
	}

	onDuplicateREPCancel() {
		this.isDuplicateModalVisible = false;
	}

	async onExposureOk() {
		this.duplicating = true;
		const currentMethodologyExposureIds = this.repList.map((c) => (c as ContentExposureModel).id);

		if (currentMethodologyExposureIds.length === 0) {
			MessageHelper.Message(
				NotificationType.Warning,
				this.formatMessage(this.repLocale.REPExposureDuplicateExposureWarning)
			);
			return;
		}

		await this.onCopyREP(currentMethodologyExposureIds, true);
		// this.resetModal();
		this.duplicating = false;
		this.isDuplicateModalVisible = false;
	}

	handleChangeduplicateTargetLPV(lpvId: string) {
		this.duplicateTargetLPVId = lpvId;
	}

	resetModal() {
		this.repExposureDuplicateForm.resetFields();
		this.isDuplicateModalVisible = false;
		this.duplicateTargetLPVId = this.targetDuplicateLPVs[0].id;
	}

	onManageOrder(currentExposure) {
		this.currentSelectedExposureId = currentExposure.id;
		this.manageOrderDrawerVisible = true;
	}

	onManageDrawerClose() {
		this.manageOrderDrawerVisible = false;
		this.currentSelectedExposureId = null;
	}

	get isReader() {
		const { editedContentDetails } = this.titleListModule;
		return editedContentDetails.languageSkillId === LanguageSkill.Read;
	}
}
